<template>

  <v-container fluid class="user-settings-profile">

    <!-------------------------->
    <!-- User Info Management -->
    <!-------------------------->
    <v-container fluid class="pt-4 pb-9 page-content" v-if="userToEdit">
      <!-- User info title -->
      <v-row class="pa-0 ma-0">
        <v-col cols="12">
          <span class="title">User Info</span>
        </v-col>
      </v-row>

      <!-- First name -->
      <v-row align="center" class="pa-0 ma-0">
        <v-col cols="12" xs="12" sm="12" md="3" lg="1">
          <span class="label">First name:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="9" md="4" lg="3">
          <v-text-field
              outlined
              hide-details
              :disabled="isUserInfoSaving"
              :rules="$root.rules.fieldRequired"
              @input="setDirty('first_name')"
              v-model="userToEdit.first_name" />
        </v-col>
      </v-row>

      <!-- Last name -->
      <v-row align="center" class="pt-0 ma-0">
        <v-col cols="12" xs="12" sm="12" md="3" lg="1">
          <span class="label">Last name:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="9" md="4" lg="3">
          <v-text-field
              outlined
              hide-details
              :disabled="isUserInfoSaving"
              :rules="$root.rules.fieldRequired"
              @input="setDirty('last_name')"
              v-model="userToEdit.last_name" />
        </v-col>
      </v-row>

      <!-- Timezone -->
      <v-row align="center" class="pa-0 ma-0 mt-3">
        <v-col cols="12" xs="12" sm="12" md="3" lg="1">
          <span class="label">Time zone:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="9" md="4" lg="3">
          <v-select
              outlined
              hide-details
              :items="availableTimeZones"
              v-model="activeTimeZone" />
        </v-col>
      </v-row>

      <!-- Phone number -->
      <v-row align="center" class="pa-0 ma-0 mt-3">
        <v-col cols="12" xs="12" sm="12" md="3" lg="1">
          <span class="label">Phone:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="5" lg="4">
          <vue-phone-number-input
              v-if="isEditingPhone"
              dark
              clearable
              :disabled="isUserInfoSaving"
              :translations="{ phoneNumberLabel: 'Phone'}"
              :no-country-selector="true"
              default-country-code="US"
              v-model="userToEdit.phone_number" />
          <span v-else>{{ activeUser.phone_number }}</span>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="2" lg="2">
          <v-btn @click="editPhone" v-if="!isEditingPhone">Change Phone</v-btn>
        </v-col>
      </v-row>

      <!-- Email -->
      <v-row align="center" class="pa-0 ma-0 mt-5">
        <v-col cols="12" xs="12" sm="12" md="3" lg="1">
          <span class="label">Email:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="5" lg="4">
          <v-text-field
              outlined
              hide-details
              v-if="isEditingEmail"
              :disabled="isUserInfoSaving"
              :rules="$root.rules.fieldRequired"
              v-model="userToEdit.email" />
          <span v-else>{{ activeUser.email }}</span>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="2" lg="2" v-if="!disableChangeEmail">
          <v-btn
              v-if="!isEditingEmail"
              @click="editEmail">
            Change Email
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="divider my-10 ml-4 mr-5"></v-row>

      <!-- User info actions (cancel, save) -->
      <v-row class="mt-0">
        <v-col cols="6" class="pl-8 pt-0">
          <v-btn
              v-if="isDirty && !isUserInfoSaving"
              color="gray"
              width="100"
              @click="cancel">
            Cancel
          </v-btn>
        </v-col>
        <v-col cols="6" class="pr-8 pt-0" align="right">
          <v-btn
              v-if="isDirty && !isUserInfoSaving"
              color="primary"
              width="110"
              @click="saveUserInfo">
            Save
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <!--------------------->
    <!-- Saved Signature -->
    <!--------------------->
    <v-container fluid class="mt-5 pt-4 pb-7 page-content" v-if="userToEdit">
      <!-- Manage saved signature title -->
      <v-row class="pa-0 ma-0">
        <v-col cols="12">
          <span class="title">Saved Signature</span>
        </v-col>
      </v-row>

      <v-row class="pa-0 ma-0 mt-2">
        <!-- Saved signature -->
        <v-col cols="12" align="left">
          <v-progress-circular indeterminate :size="40" color="primary" style="margin:10px 0 0 50px;" v-if="isSignatureLoading" />
          <div class="saved-signature-area" v-else-if="savedSignature">
            <img :src="savedSignature" />
          </div>
        </v-col>
      </v-row>
      <v-row class="pa-0 ma-0">
        <v-col cols="12">
          <!-- Signature UI (dialog) -->
          <v-dialog fullscreen persistent v-model="isManageSignatureOpen" v-if="!isSignatureLoading">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on">
                <span v-if="savedSignature">Change signature</span>
                <span v-else>Save your signature</span>
              </v-btn>
            </template>
            <user-signature
                title="Provide Signature"
                assetKey="signature"
                v-on:canceled="isManageSignatureOpen = false"
                v-on:saved="handleSavedSignature" />
          </v-dialog>
        </v-col>
      </v-row>
    </v-container>

    <!--------------------->
    <!-- Change Password -->
    <!--------------------->
    <v-container fluid class="mt-5 pt-4 pb-7 page-content" v-if="userToEdit">
      <!-- Change password title -->
      <v-row class="pa-0 ma-0">
        <v-col cols="12">
          <span class="title">Change Password</span>
        </v-col>
      </v-row>

      <!-- Change password handling -->
      <template v-if="isEditingPassword">
        <v-row align="center" class="pa-0 ma-0 mt-3">
          <v-col cols="12" xs="12" sm="10" md="5" lg="4" class="d-flex flex-column align-left justify-center">
            <div class="mb-2">Existing password:</div>
            <v-text-field
                outlined
                hide-details
                :append-icon="showExistingPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showExistingPassword ? 'text' : 'password'"
                @click:append="showExistingPassword = !showExistingPassword"
                :disabled="isPasswordChangeSaving"
                :rules="$root.rules.fieldRequired"
                v-model="existingPassword" />
          </v-col>
        </v-row>
        <v-row class="pa-0 ma-0 mt-5">
          <v-col cols="12" xs="12" sm="10" md="5" lg="4">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="password-requirements-text d-flex align-center">
                  Password requirements
                  <v-icon style="font-size:17px;" color="#34b3db" class="pl-2">mdi-information-outline</v-icon>
                </div>
              </template>
              <div>
                <span>Must be at least 8 characters, and contain:</span><br />
                <ul class="mt-1">
                  <li>1 number</li>
                  <li>1 uppercase letter</li>
                  <li>1 lowercase letter</li>
                  <li>1 special character</li>
                </ul>
              </div>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row align="center" class="pa-0 ma-0">
          <v-col cols="12" xs="12" sm="10" md="5" lg="4" class="d-flex flex-column align-left justify-center">
            <div class="mb-2">New password:</div>
            <v-text-field
                outlined
                hide-details
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                :disabled="isPasswordChangeSaving"
                :rules="$root.rules.fieldRequired"
                v-model="newPassword" />
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="1" lg="1"></v-col>
          <v-col cols="12" xs="12" sm="10" md="5" lg="4" class="d-flex flex-column align-left justify-center">
            <div class="mb-2">Confirm new password:</div>
            <v-text-field
                outlined
                hide-details
                :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showConfirmPassword ? 'text' : 'password'"
                @click:append="showConfirmPassword = !showConfirmPassword"
                @input="comparePasswords"
                :disabled="isPasswordChangeSaving"
                :rules="$root.rules.fieldRequired"
                v-model="newConfirmPassword" />
          </v-col>
        </v-row>
        <!-- (password entries do not match error) -->
        <v-row align="center" class="pa-0 ma-0" v-if="newPasswordDoesntMatch">
          <v-col cols="12" class="pb-0 d-flex align-center">
            <v-icon color="red" class="pr-2">mdi-alert-circle-outline</v-icon>
            <div class="error-text">New passwords do not match</div>
          </v-col>
        </v-row>
        <!-- (password change error) -->
        <v-row align="center" class="pa-0 ma-0" v-if="passwordChangeError">
          <v-col cols="12" class="pb-0 d-flex align-center">
            <v-icon color="red" class="pr-2">mdi-alert-circle-outline</v-icon>
            <div class="error-text">{{ passwordChangeError }}</div>
            <v-btn class="ml-6" @click="passwordChangeError = null">Ok</v-btn>
          </v-col>
        </v-row>
        <!-- (actions) -->
        <v-row align="center" class="pa-0 ma-0 mt-2">
          <v-col cols="12" class="d-flex align-center justify-start">
            <v-btn
                v-if="!passwordChangeSuccessMessage"
                plain
                color="grey"
                class="mr-10"
                :disabled="isPasswordChangeSaving"
                @click="cancelChangePassword">
              Cancel
            </v-btn>
            <v-btn
                v-if="canSavePasswordChange && !passwordChangeSuccessMessage"
                color="primary"
                width="120"
                :disabled="isPasswordChangeSaving"
                @click="savePasswordChange">
              <span v-if="isPasswordChangeSaving">Changing...</span>
              <span v-else>Save</span>
            </v-btn>
            <div class="pl-2" v-if="passwordChangeSuccessMessage">
              <v-icon color="green" class="mr-1 pb-1">mdi-check-bold</v-icon>
              <span style="color:#4caf4f;font-size:1.08rem;">{{ passwordChangeSuccessMessage }}</span>
              <v-btn
                  text
                  color="grey"
                  class="ml-10"
                  @click="cancelChangePassword">
                Close
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row align="center" class="pa-0 ma-0 mt-3">
          <v-col cols="12" xs="12" sm="12" md="5" lg="4">
            <v-btn @click="changePassword">
              Change Password
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </v-container>

  </v-container>

</template>

<script>
import { Auth } from 'aws-amplify'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import VuePhoneNumberInput from 'vue-phone-number-input'
import UserSignature from '@/components/users/UserSignature'

export default {
  name: 'UserSettingsProfile',

  components: {
    VuePhoneNumberInput,
    UserSignature
  },

  data () {
    return {
      // --- NOT YET IMPLEMENTED !!!
      disableChangeEmail: true,
      // ------------ manage user info
      isDirty: false,
      dirtyProps: [],
      isUserInfoSaving: false,
      saveErrorMessage: null,
      isEditingEmail: false,
      isEditingPhone: false,
      // ------------ hard-coded time zone
      activeTimeZone: 'America/Phoenix',
      // ------------ manage saved signature
      isSignatureLoading: false,
      loadingErrorMessage: null,
      savedSignature: null,
      isManageSignatureOpen: false,
      // ------------ manage password
      isEditingPassword: false,
      isPasswordChangeSaving: false,
      passwordChangeError: null,
      showPassword: false,
      showExistingPassword: false,
      showConfirmPassword: false,
      existingPassword: null,
      newPassword: null,
      newConfirmPassword: null,
      newPasswordDoesntMatch: false,
      passwordChangeSuccessMessage: null
    }
  },

  computed: {
    activeUser () {
      return this.$store.getters['auth/activeUser']
    },
    userToEdit () {
      return this.$store.getters['auth/activeUserEditing']
    },
    availableTimeZones () {
      return ['America/Phoenix']
    },
    canSavePasswordChange () {
      return this.existingPassword && this.newPassword && this.newConfirmPassword && !this.newPasswordDoesntMatch
    }
  },

  async beforeMount () {
    this.loadSavedSignature()
  },

  methods: {
    // Manage dirty state
    setDirty (propName) {
      if (this.activeUser[propName] !== this.userToEdit[propName]) {
        if (!this.$root.utils.includes(this.dirtyProps, propName)) this.dirtyProps.push(propName)
        this.isDirty = true
      } else {
        const dirtyPropIndex = this.dirtyProps.findIndex(prop => prop === propName)
        if (dirtyPropIndex !== -1) this.dirtyProps.splice(dirtyPropIndex, 1)
        if (this.dirtyProps.length === 0) this.isDirty = false
      }
    },

    // Cancel changes
    cancel () {
      this.isEditingEmail = false
      this.isEditingPhone = false
      this.isEditingPassword = false
      this.$store.dispatch('auth/setActiveUserToEdit') // reset user info
      this.dirtyProps = []
      this.isDirty = false
    },

    // Enable edit email
    editEmail () {
      this.isEditingEmail = true
      this.dirtyProps.push('email')
      this.isDirty = true
    },

    // Enable edit phone number
    editPhone () {
      this.isEditingPhone = true
      this.dirtyProps.push('phone_number')
      this.isDirty = true
    },

    // Load saved signature
    async loadSavedSignature () {
      this.isSignatureLoading = true

      try {
        const signature = await this.$root.fetchUserAsset('signature')
        this.savedSignature = signature
      } catch (err) {
        this.loadingErrorMessage = err.message || err.text || err
      } finally {
        this.isSignatureLoading = false
      }
    },

    // Handle saved signature
    handleSavedSignature (data = {}) {
      // Mark saved and load signature for display
      if (data.uploaded === true) {
        this.loadSavedSignature()
        this.userToEdit.saved_signature = true
        this.saveUserInfo()
      }

      // Close dialog
      this.isManageSignatureOpen = false
    },

    // Enable change password
    changePassword () {
      this.isEditingPassword = true
    },

    // Compare new passwords to ensure they match
    comparePasswords () {
      if (this.newPassword !== this.newConfirmPassword) this.newPasswordDoesntMatch = true
      else this.newPasswordDoesntMatch = false
    },

    // Cancel change password
    cancelChangePassword () {
      this.isEditingPassword = false
      this.passwordChangeError = null
      this.existingPassword = null
      this.newPassword = null
      this.newConfirmPassword = null
      this.newPasswordDoesntMatch = false
      this.passwordChangeSuccessMessage = null
    },

    // Save password change
    async savePasswordChange () {
      if (!this.canSavePasswordChange) return false
      this.isPasswordChangeSaving = true

      try {
        const user = await Auth.currentAuthenticatedUser()
        await Auth.changePassword(user, this.existingPassword, this.newPassword)
        this.passwordChangeSuccessMessage = 'Changed!'
      } catch (err) {
        this.passwordChangeError = err.message || err.text || err
      } finally {
        this.isPasswordChangeSaving = false
      }
    },

    // Save user info changes
    async saveUserInfo () {
      this.isUserInfoSaving = true

      try {
        // Update user
        await this.$store.dispatch('auth/updateUser')

        // Clear dirty
        this.dirtyProps = []
        this.isDirty = false
      } catch (err) {
        this.saveErrorMessage = err.message || err.text || err
      } finally {
        this.isUserInfoSaving = false
      }
    }
  }
}
</script>

<style scoped>
  .user-settings-profile .saved-signature-area {
    max-width: 200px;
    height: 100px;
    padding: 0;
    background-color: #ffffff;
    border-radius: 4px;
  }

  .user-settings-profile .saved-signature-area img {
    height: 100px;
  }

  .user-settings-profile .password-requirements-text {
    cursor: pointer;
    font-size: 1.0rem;
    color: #34b3db;
  }
</style>
